import type { ChavePermissao, UsuarioSessao } from '@crm/shared'

/**
 * Mesma regra do servidor: o proprietário passa por tudo.
 *
 * Isto aqui decide só o que a interface mostra. Quem realmente barra é a API:
 * esconder botão não é controle de acesso.
 */
const CHAVES_SO_FARO = new Set<string>([
  'painel.ver',
  'painel.editar',
  'estatisticas.ver',
  'estatisticas.exportar',
  'marketing.ver',
  'marketing.criar',
  'marketing.editar',
  'marketing.excluir',
  'marketing.aprovar',
  'marketing.perfis',
  'anuncios.ver',
  'anuncios.editar',
])

export function pode(usuario: UsuarioSessao | undefined, chave: ChavePermissao): boolean {
  if (!usuario) return false
  // início: inquilino (ADR 0079)
  if (usuario.organizacaoTipo === 'escola' && CHAVES_SO_FARO.has(chave)) return false
  if (usuario.proprietario && usuario.organizacaoTipo !== 'escola') {
    return true
  }
  if (usuario.papelOrganizacao === 'dono_escola') return !CHAVES_SO_FARO.has(chave)
  // fim: inquilino (ADR 0079)
  return usuario.proprietario || usuario.permissoes.includes(chave)
}

/**
 * Alguma parte da Configuração do CRM abre para a pessoa (ADR 0038): Equipe,
 * Atendimento (Números, Chatbot, Respostas rápidas, Materiais), Vendas (Funis,
 * Etiquetas, Campos) e Sistema. Equipe e Sistema aparecem também na
 * Administração (ADR 0059 e 0068).
 */
export function abreConfiguracao(usuario: UsuarioSessao | undefined): boolean {
  const chaves: ChavePermissao[] = ['configuracao.ver', 'gerenciar_chatbot', 'materiais.editar', 'api_oficial']
  // Equipe e Sistema voltaram para a Configuração do CRM (ADR 0068).
  return chaves.some((chave) => pode(usuario, chave)) || abreConfiguracaoDaAdministracao(usuario)
}

/**
 * Alguma parte da Configuração da Administração abre (ADR 0059): Equipe
 * (Usuários, Setores) e Sistema (Regras, Avisos, Importar e exportar, Logs).
 */
export function abreConfiguracaoDaAdministracao(usuario: UsuarioSessao | undefined): boolean {
  const chaves: ChavePermissao[] = [
    'configuracao.ver',
    'gerenciar_usuarios',
    'avisos.ver',
    'contatos.importar',
    'contatos.exportar',
    'empresas.importar',
    'empresas.exportar',
    'logs.ver',
    'logs.apagar',
  ]
  return chaves.some((chave) => pode(usuario, chave))
}

/**
 * A primeira tela do CRM para a pessoa: a do dia a dia que ela pode abrir.
 * `null` quando nenhuma tela do CRM abre (quem usa só o Marketing).
 */
export function rotaInicialDoCrm(usuario: UsuarioSessao): string | null {
  if (pode(usuario, 'bate_papo.ver')) return '/bate-papo'
  if (pode(usuario, 'leads.ver')) return '/leads'
  if (pode(usuario, 'calendario.ver')) return '/calendario'
  if (abreConfiguracao(usuario)) return '/configuracao'
  return null
}

/**
 * FaroEscolar Administração (ADR 0059): o Painel do admin, as Estatísticas e
 * a Configuração de equipe e sistema. `null` quando nada dela abre.
 */
export function rotaInicialDaAdministracao(usuario: UsuarioSessao): string | null {
  if (usuario.organizacaoTipo === 'escola') return null
  if (pode(usuario, 'painel.ver')) return '/admin/painel'
  if (pode(usuario, 'estatisticas.ver')) return '/admin/estatisticas'
  if (abreConfiguracaoDaAdministracao(usuario)) return '/admin/configuracao'
  return null
}

/**
 * A primeira tela do FaroEscolar Marketing (ADR 0057): o Calendário de posts,
 * ou Anúncios para quem só vê anúncios (Anúncios mudou do CRM para cá, ADR
 * 0068). `null` quando nada do Marketing abre.
 */
export function rotaInicialDoMarketing(usuario: UsuarioSessao): string | null {
  if (usuario.organizacaoTipo === 'escola') return null
  if (pode(usuario, 'marketing.ver')) return '/marketing'
  if (pode(usuario, 'anuncios.ver')) return '/marketing/anuncios'
  return null
}

export function abreMarketing(usuario: UsuarioSessao): boolean {
  return rotaInicialDoMarketing(usuario) !== null
}
